import { Box, Button, chakra, createListCollection, Grid, Input, ListCollection, Select } from '@chakra-ui/react';
import { LogMessageRow } from './LogMessageRow';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SocketLogMessage, SocketLogMessageLevel } from './FriendlyLogger';
import { simpleClone } from '@/common/utils';
import { formatTimestamp } from './logCommon';

const Wrapper = chakra('div', {
  base: {
    padding: '20px',
    position: 'relative',
  },
});

const Content = chakra('div', {
  base: {
    borderRadius: 'small',
    border: '1px solid {colors.grey.light.3}',
    minHeight: '100%',
    maxHeight: '100%',
    height: 'calc(100vh - 120px - 40px - 40px)',
    overflow: 'hidden',
    overflowY: 'auto',
    backgroundColor: 'grey.light.0',
  },
});

const ClearButton = chakra('div', {
  base: {
    color: 'grey.light.3',
    cursor: 'pointer',

    position: 'absolute',
    right: '10px',
    top: '0px',

    fontSize: '24px',

    transitionDuration: 'slow',
    transitionBehavior: 'linear',
    transitionProperty: 'color',

    _hover: {
      color: 'grey.dark.2',
    },
  },
});

interface WebsocketLogProps {
  logSlice: SocketLogMessage[];
}

type LogLevelItem = { value: SocketLogMessageLevel; label: string };

export const WebsocketLog = ({ logSlice }: WebsocketLogProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'planner.log' });
  const levelItemList: LogLevelItem[] = useMemo(
    () => [
      { value: 'info', label: t('level.info') },
      { value: 'warn', label: t('level.warn') },
      { value: 'success', label: t('level.success') },
      { value: 'error', label: t('level.error') },
    ],
    [t],
  );

  const [filter, setFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState<SocketLogMessageLevel[]>([]);

  const sortedSlice = simpleClone(logSlice).sort((a, b) => Temporal.ZonedDateTime.compare(b.timestamp, a.timestamp));

  const filteredSlice = sortedSlice
    .filter((row) => !filter || row.message.toLocaleLowerCase().includes(filter.toLocaleLowerCase()))
    .filter((row) => levelFilter.length === 0 || levelFilter.includes(row.level));
  const levelCollection: ListCollection<LogLevelItem> = useMemo(
    () =>
      createListCollection({
        items: levelItemList,
      }),
    [levelItemList],
  );

  const copyAllMessages = useCallback(() => {
    const rows = filteredSlice.map(
      (row) =>
        `${formatTimestamp(row.timestamp)} [${row.level.substring(0, 5).toUpperCase().padStart(5)}] ${row.message}`,
    );
    const fullText = rows.join('\n');

    navigator.clipboard.writeText(fullText);
  }, [filteredSlice]);

  return (
    <Wrapper>
      <Grid templateColumns="55fr 30fr 15fr" gap="10px">
        <Box position="relative">
          <Input
            placeholder={t('filter')}
            sizing="fill-width"
            mb="10px"
            size="compact"
            value={filter}
            onChange={(e) => setFilter(e.target.value || '')}
          />
          <ClearButton onClick={() => setFilter('')}>&times;</ClearButton>
        </Box>
        <Box minWidth={0}>
          <Select.Root
            collection={levelCollection}
            variant="compact"
            multiple
            value={levelFilter}
            onValueChange={(det) => setLevelFilter(det.value as SocketLogMessageLevel[])}
          >
            <Select.HiddenSelect />
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder={t('filterLevel')} />
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {levelCollection.items.map((item) => (
                  <Select.Item item={item} key={item.value}>
                    {item.label}
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
        <Box minWidth={0}>
          <Button size="compact" width="100%" onClick={copyAllMessages}>
            {t('copyAll')}
          </Button>
        </Box>
      </Grid>
      <Content>
        {filteredSlice.map((row) => (
          <LogMessageRow row={row} key={row.id} />
        ))}
      </Content>
    </Wrapper>
  );
};
